import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { ExportButton } from '@/components/common/ExportButton';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { TAKA_SYMBOL } from '@/constants/app.constants';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { formatCount } from '@/utils/number';
import { mpApprovalsInfiniteQueryOptions } from '../api/marketPromotion.api';
import { approvalTotalsQueryOptions, exportCashCollection } from '../api/marketPromotionP2.api';
import { DonationApprovalFilterPanel } from '../components/DonationApprovalFilterPanel';
import { getMpApprovalColumns } from '../components/MpColumns';
import { ViewDonationDialog } from '../components/ViewDonationDialog';
import { DONATION_UPCOMING_SEARCH } from '../constants/marketPromotion.constants';
import {
  useApprovalReportFilters,
  type ApprovalReportRouteId,
} from '../hooks/useApprovalReportFilters';
import type { MpApprovalRow } from '../types/marketPromotion.types';

interface ApprovalReportLayoutProps {
  title: string;
  routeId: ApprovalReportRouteId;
}

export function ApprovalReportLayout({ title, routeId }: ApprovalReportLayoutProps) {
  const controller = useApprovalReportFilters(routeId);
  const { search } = controller;

  const { rows, isLoading, loadError, refetch, loadMoreProps } = useInfiniteTableQuery(
    mpApprovalsInfiniteQueryOptions(search, false),
  );
  const { data: totals, isLoading: isTotalsLoading } = useQuery(approvalTotalsQueryOptions(search));

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [viewing, setViewing] = useState<MpApprovalRow | null>(null);

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title={title}
        titleAddon={<DateRangeBadge dateFrom={search.dateFrom} dateTo={search.dateTo} />}
        search={DONATION_UPCOMING_SEARCH}
        actions={
          <>
            <AdvancedFilterButton
              isOpen={isFilterOpen}
              activeCount={controller.activeFilterCount}
              onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
            />
            <ExportButton
              onExport={() => exportCashCollection(search)}
              disabled={isLoading || loadError !== null}
            />
          </>
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <DonationApprovalFilterPanel filters={controller} />
      </FilterPanelCollapse>

      <StatCardGrid
        isLoading={isTotalsLoading}
        stats={(totals ?? []).map((row) => ({
          key: row.paymentType,
          label: `${row.paymentType} · ${formatCount(row.donations, 'donation')}`,
          value: Math.round(row.approved),
          unit: TAKA_SYMBOL,
        }))}
      />

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the report"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={getMpApprovalColumns({ onView: setViewing }, false)}
          isLoading={isLoading}
          className="lg:flex-1"
          stickyColumnId="instituteName"
          emptyMessage="No donation matches"
          {...loadMoreProps}
        />
      )}

      <ViewDonationDialog
        donation={viewing}
        onOpenChange={(open) => {
          if (!open) setViewing(null);
        }}
      />
    </div>
  );
}
